import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Confirm, DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatXaf } from '../../lib/format.js';
import { AVAILABILITY_LABEL, lettingOf } from '../../lib/letting.js';

// A unit's own status in the words the marketplace shows for it.
const UNIT_AVAILABILITY = { vacant: 'available', reserved: 'pending', occupied: 'occupied', maintenance: 'unavailable' };
const STATUS_OPTION = {
  vacant: 'Available',
  reserved: 'Pending - promised to somebody',
  occupied: 'Rented',
  maintenance: 'Under repair'
};
const capital = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const BLANK = {
  label: '',
  unitType: 'apartment',
  rooms: 1,
  bathrooms: 1,
  areaSqm: '',
  rent: '',
  depositMonths: 2,
  furnished: false,
  status: 'vacant',
  notes: ''
};

const payloadFrom = (form) => ({
  label: form.label.trim(),
  unitType: form.unitType,
  rooms: Number(form.rooms),
  bathrooms: Number(form.bathrooms),
  areaSqm: form.areaSqm ? Number(form.areaSqm) : null,
  rent: Number(form.rent),
  depositMonths: Number(form.depositMonths),
  furnished: Boolean(form.furnished),
  status: form.status,
  notes: form.notes?.trim() || null
});

const UnitForm = ({ title, initial, submitLabel, onSubmit, onClose, word = 'unit' }) => {
  const { propertyTypes, unitStatuses } = useMeta();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(payloadFrom(form));
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="unit-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : submitLabel}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="unit-form" className="stack" onSubmit={submit}>
        <div className="grid-3">
          <Field
            label={`Name of the ${word}`}
            hint={word === 'room' ? 'Room 1, Front room, Room B.' : 'Apt 1, Apt 2, Flat 3B.'}
            error={errors.label}
            required
          >
            <input type="text" value={form.label} onChange={(event) => set({ label: event.target.value })} required />
          </Field>

          <Field label="Type" error={errors.unitType}>
            <select value={form.unitType} onChange={(event) => set({ unitType: event.target.value })}>
              {propertyTypes.map((type) => (
                <option key={type.slug} value={type.slug}>
                  {type.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Monthly rent in FCFA" error={errors.rent} required>
            <input
              type="number"
              min="0"
              step="1"
              value={form.rent}
              onChange={(event) => set({ rent: event.target.value })}
              required
            />
          </Field>

          <Field label="Rooms" error={errors.rooms}>
            <input type="number" min="0" max="30" value={form.rooms} onChange={(event) => set({ rooms: event.target.value })} />
          </Field>

          <Field label="Bathrooms" error={errors.bathrooms}>
            <input
              type="number"
              min="0"
              max="20"
              value={form.bathrooms}
              onChange={(event) => set({ bathrooms: event.target.value })}
            />
          </Field>

          <Field label="Floor area in m2" error={errors.areaSqm}>
            <input type="number" min="0" value={form.areaSqm} onChange={(event) => set({ areaSqm: event.target.value })} />
          </Field>

          <Field label="Deposit in months" error={errors.depositMonths}>
            <input
              type="number"
              min="0"
              max="12"
              value={form.depositMonths}
              onChange={(event) => set({ depositMonths: event.target.value })}
            />
          </Field>

          <Field label="Furnished">
            <select
              value={form.furnished ? 'true' : 'false'}
              onChange={(event) => set({ furnished: event.target.value === 'true' })}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Field>

          <Field label="Status" error={errors.status}>
            <select value={form.status} onChange={(event) => set({ status: event.target.value })}>
              {unitStatuses.map((status) => (
                <option key={status} value={status}>
                  {STATUS_OPTION[status] || status}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Private notes" hint="Only you see this." error={errors.notes}>
          <textarea rows="2" value={form.notes} onChange={(event) => set({ notes: event.target.value })} />
        </Field>
      </form>
    </Modal>
  );
};

const BulkForm = ({ onSubmit, onClose, word = 'unit', words = 'units', defaults }) => {
  const [form, setForm] = useState({ prefix: word === 'room' ? 'Room ' : 'Apt ', startAt: 1, count: 6, ...defaults, label: undefined });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const { label, ...template } = payloadFrom({ ...form, label: 'x' });
      await onSubmit({
        prefix: form.prefix,
        startAt: Number(form.startAt),
        count: Number(form.count),
        template
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  const preview = Array.from({ length: Math.min(Number(form.count) || 0, 4) }, (_, index) =>
    `${form.prefix}${Number(form.startAt) + index}`
  );

  return (
    <Modal
      title={`Add several ${words} at once`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="bulk-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating...' : `Create ${form.count || 0} ${words}`}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        For a building where the {words} are much the same. Give the naming pattern and how many, then change any that
        differ afterwards.
      </p>

      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="bulk-form" className="stack" onSubmit={submit}>
        <div className="grid-3">
          <Field label="Name starts with" hint={`"${form.prefix}" gives ${form.prefix}1, ${form.prefix}2, ${form.prefix}3.`}>
            <input type="text" value={form.prefix} onChange={(event) => set({ prefix: event.target.value })} />
          </Field>
          <Field label="First number">
            <input type="number" min="0" value={form.startAt} onChange={(event) => set({ startAt: event.target.value })} />
          </Field>
          <Field label="How many" error={errors.count} required>
            <input
              type="number"
              min="1"
              max="60"
              value={form.count}
              onChange={(event) => set({ count: event.target.value })}
              required
            />
          </Field>
        </div>

        {preview.length > 0 ? (
          <p className="field-hint">
            This creates {preview.join(', ')}
            {Number(form.count) > 4 ? ` and so on up to ${form.prefix}${Number(form.startAt) + Number(form.count) - 1}` : ''}.
          </p>
        ) : null}

        <div className="grid-3">
          <Field label="Rent for each, in FCFA" error={errors['template.rent']} required>
            <input
              type="number"
              min="0"
              step="1"
              value={form.rent}
              onChange={(event) => set({ rent: event.target.value })}
              required
            />
          </Field>
          <Field label="Rooms in each">
            <input type="number" min="0" value={form.rooms} onChange={(event) => set({ rooms: event.target.value })} />
          </Field>
          <Field label="Bathrooms in each">
            <input
              type="number"
              min="0"
              value={form.bathrooms}
              onChange={(event) => set({ bathrooms: event.target.value })}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
};

const UnitManager = () => {
  const { id } = useParams();
  const toast = useToast();
  const { propertyTypeLabel } = useMeta();

  const property = useFetch(`/properties/${id}`, [id]);
  const units = useFetch(`/properties/${id}/units`, [id]);

  const [dialog, setDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const words = lettingOf(property.data?.property);
  const word = words.unit || 'unit';
  const plural = words.units || 'units';
  const defaults = { ...BLANK, unitType: word === 'room' ? 'single_room' : 'apartment' };

  useTitle(property.data ? `${capital(plural)} in ${property.data.property.title}` : 'Units');

  if ((property.loading && !property.data) || (units.loading && !units.data)) return <Spinner />;
  if (property.error) {
    return (
      <div className="container section">
        <ErrorState message={property.error} onRetry={property.reload} />
      </div>
    );
  }

  const items = units.data?.items || [];
  const occupied = items.filter((unit) => unit.status === 'occupied');
  const vacant = items.filter((unit) => unit.status === 'vacant');
  const monthlyIfFull = items.reduce((sum, unit) => sum + unit.rent, 0);
  const monthlyNow = occupied.reduce((sum, unit) => sum + unit.rent, 0);

  const create = async (payload) => {
    await api.post(`/properties/${id}/units`, payload);
    toast.success(`${capital(word)} added.`);
    setDialog(null);
    units.reload();
  };

  const createBulk = async (payload) => {
    const result = await api.post(`/properties/${id}/units/bulk`, payload);
    toast.success(`${result.items.length} ${plural} created.`);
    if (result.skipped.length > 0) {
      toast.info(`Skipped ${result.skipped.join(', ')} because those names were already used.`);
    }
    setDialog(null);
    units.reload();
  };

  const update = async (payload) => {
    await api.patch(`/units/${editing.id}`, payload);
    toast.success(`${capital(word)} saved.`);
    setEditing(null);
    units.reload();
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/units/${deleting.id}`);
      toast.success(`${capital(word)} removed.`);
      setDeleting(null);
      units.reload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'label',
      header: capital(word),
      render: (unit) => (
        <div className="cell-stack">
          <strong>{unit.label}</strong>
          <span className="muted">
            {propertyTypeLabel(unit.unitType)} - {unit.rooms} room{unit.rooms === 1 ? '' : 's'}, {unit.bathrooms} bath
            {unit.areaSqm ? `, ${unit.areaSqm} m2` : ''}
          </span>
          {unit.notes ? <span className="muted">{unit.notes}</span> : null}
        </div>
      )
    },
    { key: 'rent', header: 'Rent', render: (unit) => formatXaf(unit.rent) },
    {
      key: 'status',
      header: 'Status',
      render: (unit) => {
        const where = UNIT_AVAILABILITY[unit.status] || 'unavailable';
        return <StatusBadge status={where} label={AVAILABILITY_LABEL[where]} />;
      }
    },
    {
      key: 'occupant',
      header: 'Who is in it',
      render: (unit) =>
        unit.tenancy ? (
          <div className="cell-stack">
            <Link to={`/landlord/leases/${unit.tenancy.id}`}>{unit.tenancy.tenantName}</Link>
            <span className="muted">{unit.tenancy.tenantPhone}</span>
            <StatusBadge status={unit.tenancy.status === 'active' ? 'approved' : 'pending'} label={unit.tenancy.status === 'active' ? 'Lease active' : 'Lease pending'} />
          </div>
        ) : (
          <span className="muted">Nobody</span>
        )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (unit) => (
        <div className="cell-actions">
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(unit)}>
            Edit
          </button>
          {unit.tenancy ? (
            <Link to={`/landlord/leases/${unit.tenancy.id}`} className="btn btn-ghost btn-sm">
              Lease
            </Link>
          ) : (
            <Link to={`/landlord/leases/new?propertyId=${id}&unitId=${unit.id}`} className="btn btn-ghost btn-sm">
              Create lease
            </Link>
          )}
          <button type="button" className="link-btn danger-link" onClick={() => setDeleting(unit)}>
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>
            {capital(plural)} in {property.data.property.title}
          </h1>
          <p className="muted">
            {[property.data.property.neighbourhood, property.data.property.city].filter(Boolean).join(', ')}. Each{' '}
            {word} has its own rent and its own tenant, and the marketplace shows every one of them as available, pending
            or rented.
          </p>
        </div>
        <div className="row-actions">
          <Link to="/landlord/properties" className="btn btn-ghost">
            Back to listings
          </Link>
          <button type="button" className="btn btn-outline" onClick={() => setDialog('bulk')}>
            Add several
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setDialog('one')}>
            Add {word === 'apartment' ? 'an' : 'a'} {word}
          </button>
        </div>
      </header>

      {items.length > 0 ? (
        <div className="stat-row">
          <div className="stat-tile">
            <span className="stat-value">{items.length}</span>
            <span className="stat-label">{capital(plural)}</span>
          </div>
          <div className="stat-tile">
            <span className="stat-value">{occupied.length}</span>
            <span className="stat-label">Rented</span>
          </div>
          <div className="stat-tile">
            <span className="stat-value">{vacant.length}</span>
            <span className="stat-label">Available</span>
          </div>
          <div className="stat-tile">
            <span className="stat-value">{formatXaf(monthlyNow)}</span>
            <span className="stat-label">Coming in monthly</span>
            <span className="stat-note">
              {formatXaf(monthlyIfFull)} if every {word} were let
            </span>
          </div>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: `No ${plural} yet`,
          message:
            words.unit === null
              ? 'This listing is let as one house, so it does not need units: create the lease straight from the listing. To let it in parts, change it to an apartment building or rooms on the listing first.'
              : `Add each of the ${plural} with its rent. Until you do, nobody can apply, because there is nothing to choose from.`
        }}
      />

      {dialog === 'one' ? (
        <UnitForm
          title={`Add ${word === 'apartment' ? 'an' : 'a'} ${word}`}
          initial={defaults}
          submitLabel={`Add ${word}`}
          word={word}
          onSubmit={create}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'bulk' ? (
        <BulkForm word={word} words={plural} defaults={defaults} onSubmit={createBulk} onClose={() => setDialog(null)} />
      ) : null}

      {editing ? (
        <UnitForm
          title={`Edit ${editing.label}`}
          initial={{
            label: editing.label,
            unitType: editing.unitType,
            rooms: editing.rooms,
            bathrooms: editing.bathrooms,
            areaSqm: editing.areaSqm ?? '',
            rent: String(editing.rent),
            depositMonths: editing.depositMonths,
            furnished: editing.furnished,
            status: editing.status,
            notes: editing.notes ?? ''
          }}
          submitLabel={`Save ${word}`}
          word={word}
          onSubmit={update}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {deleting ? (
        <Confirm
          title={`Delete ${deleting.label}?`}
          message={`This only works if the ${word} has never been let. Once there is a tenancy on it the rent history has to stay, so mark it as under repair instead.`}
          confirmLabel={`Delete the ${word}`}
          busy={busy}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
};

export default UnitManager;
